import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { getNav } from "@/lib/navConfig";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, ClipboardList, FileText, Loader2, PlusCircle, Receipt, Wrench, CheckCircle2, DollarSign, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, isViewer } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/client").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const allActions = [
    { label: "Add Property", icon: Building2, to: "/app/client/properties", write: true },
    { label: "Request Service", icon: PlusCircle, to: "/app/client/request", write: true },
    { label: "View Work Orders", icon: ClipboardList, to: "/app/client/work-orders" },
    { label: "View Estimates", icon: FileText, to: "/app/client/estimates" },
    { label: "View Invoices", icon: Receipt, to: "/app/client/invoices" },
  ];
  const actions = isViewer ? allActions.filter((a) => !a.write) : allActions;

  return (
    <DashboardLayout nav={getNav("client", user?.org_role)} title="Client Dashboard">
      {!stats ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-properties" label="Properties" value={stats.properties} icon={Building2} />
            <StatCard testid="stat-open-wo" label="Open Work Orders" value={stats.open_work_orders} icon={ClipboardList} accent="text-purple-600" />
            <StatCard testid="stat-pending-estimates" label="Pending Estimates" value={stats.pending_estimates} icon={FileText} accent="text-amber-600" />
            <StatCard testid="stat-in-progress" label="Jobs In Progress" value={stats.jobs_in_progress} icon={Wrench} accent="text-indigo-600" />
            <StatCard testid="stat-completed" label="Completed Jobs" value={stats.completed_jobs} icon={CheckCircle2} accent="text-emerald-600" />
            <StatCard testid="stat-invoices" label="Invoices" value={stats.invoices} icon={Receipt} accent="text-teal-600" />
            <StatCard testid="stat-monthly-spend" label="Monthly Spend" value={money(stats.monthly_spend)} icon={DollarSign} accent="text-green-600" />
          </div>

          <div>
            <h2 className="font-head text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {actions.map((a) => (
                <button key={a.label} data-testid={`quick-${a.label.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => navigate(a.to)}
                  className="group bg-white border border-slate-200 rounded-xl p-5 text-left hover:border-blue-300 hover:-translate-y-1 transition-[transform,border-color] duration-200 shadow-sm">
                  <a.icon className="w-6 h-6 text-blue-600" />
                  <div className="mt-3 font-medium text-slate-900 text-sm flex items-center justify-between">{a.label}<ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
